import React, { useEffect, useMemo, useState } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import { Card, CardContent } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { computeProofOfLearning, buildLearnerEvidenceChain } from "../../../utils/proofOfLearningHelpers"
import { listOrgLearners } from "../../../utils/learnerHelpers"
import { listOrgCertificates } from "../../../utils/certHelpers"

const RANGE_OPTIONS = [
  { key: "30d", label: "30 Days" },
  { key: "12m", label: "12 Months" },
]

const MiniBarChart = ({ data }) => {
  const max = Math.max(1, ...(data || []).map((d) => d.value))
  return (
    <div className="flex items-end gap-[2px] h-8 mt-2">
      {data?.map((d, i) => (
        <div
          key={i}
          title={`${d.label}: ${d.value}`}
          className="flex-1 bg-primary/30 hover:bg-primary/60 rounded-sm transition-colors"
          style={{ height: `${Math.max(4, (d.value / max) * 100)}%` }}
        />
      ))}
    </div>
  )
}

const EvidenceStat = ({ label, total, data, icon }) => (
  <Card className="p-4 dark:bg-transparent">
    <div className="flex items-center justify-between">
      <p className="text-xs text-muted-foreground">{label}</p>
      <Icon name={icon} size={14} className="text-muted-foreground" />
    </div>
    <p className="text-2xl font-bold text-foreground mt-0.5">{total}</p>
    <MiniBarChart data={data} />
  </Card>
)

const initials = (name, email) => {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/)
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?"
  }
  return (email?.[0] || "?").toUpperCase()
}

const formatDate = (value) => {
  if (!value) return "—"
  const ms = typeof value?.toMillis === "function" ? value.toMillis() : new Date(value).getTime()
  if (Number.isNaN(ms)) return "—"
  return new Date(ms).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

const ProofOfLearningPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [overview, setOverview] = useState(null)
  const [learners, setLearners] = useState([])
  const [certificates, setCertificates] = useState([])
  const [rangeKey, setRangeKey] = useState("30d")
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [selectedLearnerId, setSelectedLearnerId] = useState(null)

  useEffect(() => {
    if (!orgId) return
    const load = async () => {
      setLoading(true)
      const [ov, { learners: l }, { certificates: c }] = await Promise.all([
        computeProofOfLearning(orgId, rangeKey),
        listOrgLearners(orgId),
        listOrgCertificates(orgId),
      ])
      setOverview(ov)
      setLearners(l || [])
      setCertificates(c || [])
      setLoading(false)
    }
    load()
  }, [orgId, rangeKey])

  const filteredLearners = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return learners
    return learners.filter(
      (l) => l.name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q)
    )
  }, [learners, search])

  const selectedLearner = learners.find((l) => l.userId === selectedLearnerId) || null
  const chain = useMemo(
    () => buildLearnerEvidenceChain(selectedLearner, certificates),
    [selectedLearner, certificates]
  )

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground text-sm">Loading Proof of Learning...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">
          The evidence infrastructure behind every credential — what each learner actually did,
          before it became a credential.
        </p>
        <div className="flex gap-1 shrink-0">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.key}
              onClick={() => setRangeKey(r.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                rangeKey === r.key
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Org-wide evidence overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <EvidenceStat
          label="Learning Records"
          total={overview?.learningRecords?.total || 0}
          data={overview?.learningRecords?.data}
          icon="BookOpen"
        />
        <EvidenceStat
          label="Achievements Recorded"
          total={overview?.achievementsRecorded?.total || 0}
          data={overview?.achievementsRecorded?.data}
          icon="Sparkles"
        />
        <EvidenceStat
          label="Assessment Evidence"
          total={overview?.assessmentEvidence?.total || 0}
          data={overview?.assessmentEvidence?.data}
          icon="ClipboardCheck"
        />
        <EvidenceStat
          label="Credential Evidence"
          total={overview?.credentialEvidence?.total || 0}
          data={overview?.credentialEvidence?.data}
          icon="Award"
        />
      </div>

      {/* Learner evidence explorer */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        <Card className="p-3 dark:bg-transparent h-fit">
          <Input
            type="text"
            placeholder="Search learners..."
            value={search}
            onChange={(e) => setSearch(e?.target?.value)}
            className="mb-3"
          />
          <div className="space-y-1 max-h-[480px] overflow-y-auto">
            {filteredLearners.map((l) => (
              <button
                key={l.userId}
                onClick={() => setSelectedLearnerId(l.userId)}
                className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left transition-colors ${
                  selectedLearnerId === l.userId ? "bg-primary/10" : "hover:bg-muted"
                }`}
              >
                <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground shrink-0">
                  {initials(l.name, l.email)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">
                    {l.name || "Unnamed Learner"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {l.coursesCount} course{l.coursesCount === 1 ? "" : "s"} · {l.credentialsCount} credential
                    {l.credentialsCount === 1 ? "" : "s"}
                  </p>
                </div>
              </button>
            ))}
            {filteredLearners.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">No learners found.</p>
            )}
          </div>
        </Card>

        <div className="min-w-0">
          {!selectedLearner ? (
            <Card className="p-12 text-center dark:bg-transparent">
              <Icon name="ShieldCheck" size={28} className="mx-auto mb-3 text-muted-foreground" />
              <h3 className="font-semibold text-foreground mb-1">Select a learner</h3>
              <p className="text-sm text-muted-foreground">
                Pick a learner on the left to inspect their evidence chain.
              </p>
            </Card>
          ) : chain.length === 0 ? (
            <Card className="p-12 text-center dark:bg-transparent">
              <p className="text-sm text-muted-foreground">
                {selectedLearner.name || "This learner"} has no enrollments yet.
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {chain.map((course) => (
                <Card key={course.courseId} className="dark:bg-transparent overflow-hidden">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{course.courseTitle}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="h-1.5 w-24 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full"
                              style={{ width: `${course.progressPercent}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">{course.progressPercent}%</span>
                        </div>
                      </div>
                      <Badge variant={course.completed ? "success" : "secondary"}>
                        {course.completed ? "Completed" : "In Progress"}
                      </Badge>
                    </div>

                    {/* Evidence chain: assessments */}
                    {course.assessments.length > 0 && (
                      <div className="pl-3 border-l-2 border-border space-y-2">
                        {course.assessments.map((a) => (
                          <div key={a.key} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 text-foreground">
                              <Icon
                                name={a.type === "capstone" ? "Trophy" : "FileCheck"}
                                size={13}
                                className="text-muted-foreground"
                              />
                              <span className="capitalize">
                                {a.type === "capstone" ? "Capstone Assessment" : `Module: ${a.key}`}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{a.score !== null ? `${a.score}%` : "—"}</span>
                              <span>·</span>
                              <span>{a.attempts} attempt{a.attempts === 1 ? "" : "s"}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Evidence chain: credential */}
                    <div className="pl-3 border-l-2 border-border">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Icon
                            name="Award"
                            size={13}
                            className={course.credential ? "text-primary" : "text-muted-foreground"}
                          />
                          {course.credential ? (
                            <span className="text-foreground">
                              Credential issued —{" "}
                              <span className="font-mono text-xs text-muted-foreground">
                                {course.credential.certId}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">No credential issued yet</span>
                          )}
                        </div>
                        {course.credential && (
                          <span className="text-xs text-muted-foreground">
                            {formatDate(course.credential.issueDate)}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProofOfLearningPanel